﻿export enum StreamingUpdateReason {
    Unknown,
    ThreadCreated,
    RunCreated,
    RunQueued,
    RunInProgress,
    RunRequiresAction,
    RunCompleted,
    RunIncomplete,
    RunFailed,
    RunCancelling,
    RunCancelled,
    RunExpired,
    RunStepCreated,
    RunStepInProgress,
    RunStepUpdated,
    RunStepCompleted,
    RunStepFailed,
    RunStepCancelled,
    RunStepExpired,
    MessageCreated,
    MessageInProgress,
    MessageUpdated,
    MessageCompleted,
    MessageFailed,
    Error,
    Done
}

export enum MessageContentPartKind {
    Text = 0,
    Image = 1,
    Refusal = 2
}

export enum MessageRole {
    User,
    Assistant
}

export enum MessageImageDetail {
    Auto,
    Low,
    High
}

export type MessageStatus = 'in_progress' | 'incomplete' | 'completed';

export type MessageFailureReason =
    | 'content_filter'
    | 'max_tokens'
    | 'run_cancelled'
    | 'run_expired'
    | 'run_failed';

export interface TextAnnotation {
    startIndex: number;
    endIndex: number;
    textToReplace?: string;
    inputFileId?: string;
    outputFileId?: string;
}

export interface MessageContent {
    imageUri?: string;
    imageFileId?: string;
    imageDetail?: MessageImageDetail;
    text?: string;
    textAnnotations?: TextAnnotation[];
    refusal?: string;
}

export interface MessageCreationAttachment {
    fileId: string;
}

export interface MessageFailureDetails {
    reason: MessageFailureReason;
}

export interface ThreadMessage {
    id: string;
    createdAt: string;
    threadId: string;
    status: MessageStatus;
    incompleteDetails?: MessageFailureDetails;
    completedAt?: string;
    incompleteAt?: string;
    role: MessageRole;
    content: MessageContent[];
    assistantId?: string;
    runId?: string;
    attachments?: MessageCreationAttachment[];
    metadata?: Record<string, string>;
}

interface StreamingUpdateBase {
    updateKind: StreamingUpdateReason;
}

export interface MessageStatusUpdateType extends StreamingUpdateBase {
    updateKind:
        | StreamingUpdateReason.MessageCreated
        | StreamingUpdateReason.MessageInProgress
        | StreamingUpdateReason.MessageCompleted
        | StreamingUpdateReason.MessageFailed;
    value: ThreadMessage;
}

export interface MessageContentUpdateType extends StreamingUpdateBase {
    updateKind: StreamingUpdateReason.MessageUpdated;
    messageId: string;
    messageIndex: number;
    role?: MessageRole;
    imageFileId?: string;
    imageDetail?: MessageImageDetail;
    text?: string;
    textAnnotation?: TextAnnotation;
    refusalUpdate?: string;
}

export interface RunUpdateType extends StreamingUpdateBase {
    updateKind:
        | StreamingUpdateReason.RunCreated
        | StreamingUpdateReason.RunQueued
        | StreamingUpdateReason.RunInProgress
        | StreamingUpdateReason.RunCompleted
        | StreamingUpdateReason.RunIncomplete
        | StreamingUpdateReason.RunFailed
        | StreamingUpdateReason.RunCancelling
        | StreamingUpdateReason.RunCancelled
        | StreamingUpdateReason.RunExpired;
    value: ThreadRun;
}

export interface RunStepUpdateType extends StreamingUpdateBase {
    updateKind:
        | StreamingUpdateReason.RunStepCreated
        | StreamingUpdateReason.RunStepInProgress
        | StreamingUpdateReason.RunStepCompleted
        | StreamingUpdateReason.RunStepFailed
        | StreamingUpdateReason.RunStepCancelled
        | StreamingUpdateReason.RunStepExpired;
    value: RunStep;
}

interface RunStepDetailsUpdateType extends StreamingUpdateBase {
    updateKind: StreamingUpdateReason.RunStepUpdated;
    stepId: string;
    createdMessageId?: string;
    toolCallId?: string;
    toolCallIndex?: number;
    codeInterpreterInput?: string;
    codeInterpreterOutputs?: RunStepCodeInterpreterOutput[];
    functionName?: string;
    functionArguments?: string;
    functionOutput?: string;
}

interface RequiredActionUpdateType extends StreamingUpdateBase {
    updateKind: StreamingUpdateReason.RunRequiresAction;
    value: ThreadRun;
    functionName?: string;
    functionArguments?: string;
    toolCallId?: string;
}

interface ThreadUpdateType extends StreamingUpdateBase {
    updateKind: StreamingUpdateReason.ThreadCreated;
    value: AssistantThread;
}

interface DoneUpdateType extends StreamingUpdateBase {
    updateKind: StreamingUpdateReason.Done;
}

interface ErrorUpdateType extends StreamingUpdateBase {
    updateKind: StreamingUpdateReason.Error;
}

export type StreamingUpdate =
    | MessageStatusUpdateType
    | MessageContentUpdateType
    | RunUpdateType
    | RunStepUpdateType
    | RunStepDetailsUpdateType
    | RequiredActionUpdateType
    | ThreadUpdateType
    | DoneUpdateType
    | ErrorUpdateType;

interface ThreadRun {
    requiredActions?: RequiredAction[];
    threadId: string;
}

interface RunStep {
    details: RunStepDetails;
    id: string;
    createdAt: string;
    assistantId: string;
    threadId: string;
    runId: string;
    metadata?: Record<string, string>;
    usage: Usage;
}

interface Usage {
    outputTokenCount: number;
    inputTokenCount: number;
    totalTokenCount: number;
}

interface RunStepDetails {
    createdMessageId: string | null;
    toolCalls: ToolCall[];
}

interface ToolCall {
    toolCallId: string;
    codeInterpreterInput: null;
    codeInterpreterOutputs: unknown[];
    functionName: null;
    functionArguments: null;
    functionOutput: null;
    fileSearchRanker: Record<string, never>;
    fileSearchScoreThreshold: number;
    fileSearchResults: FileSearchResult[];
    toolKind: number;
}

export interface FileSearchResult {
    fileId: string;
    fileName: string;
    score: number;
}

interface AssistantThread {
    id: string;
    metadata: Record<string, string>;
    createdAt: string;
    toolResources: ToolResources;
}

interface RequiredAction {}

interface RunStepCodeInterpreterOutput {}

interface ToolResources {}

export interface CitationInfo {
    fileId: string;
    fileName: string;
    score: number;
    title: string;
    url?: string;
    content: string;
    followUpQuestions: string[];
    citationType: CitationType;
}

export enum CitationType {
    QA,
    Article,
    Web = 2
}

export interface ThreadInfo {
    threadId: string;
    fileSearchResults: FileSearchResult[];
}
